import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export const createPropertySection = createAsyncThunk<any, any>(
  "propertySections/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await ipponApi.post(
        `${BaseURL}/api/v1/property-sections`,
        payload
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to create property section"
      );
    }
  }
);

export const fetchPropertySectionsByProperty = createAsyncThunk<
  any,
  { property_id: string; property_section_types?: string }
>(
  "propertySections/fetchByProperty",
  async ({ property_id, property_section_types }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/property-sections`, {
        params: {
          property_id,
          ...(property_section_types ? { property_section_types } : {}),
        },
      });
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to fetch property sections"
      );
    }
  }
);

export const fetchPropertySectionById = createAsyncThunk<
  any,
  { property_section_id: string }
>(
  "propertySections/fetchById",
  async ({ property_section_id }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(
        `${BaseURL}/api/v1/property-sections/${property_section_id}`
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to fetch property section"
      );
    }
  }
);

export const appendContract = createAsyncThunk<
  any,
  { property_section_id: string; body: any }
>(
  "propertySections/appendContract",
  async ({ property_section_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.post(
        `${BaseURL}/api/v1/property-sections/${property_section_id}/contracts`,
        body
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to append contract"
      );
    }
  }
);

export const updateCommonAreaSection = createAsyncThunk<
  any,
  { property_section_id: string; body: any }
>(
  "propertySections/updateCommonArea",
  async ({ property_section_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.patch(
        `${BaseURL}/api/v1/property-sections/${property_section_id}/common-area`,
        body
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to update common area"
      );
    }
  }
);

export const updatePropertySectionMeta = createAsyncThunk<
  any,
  {
    property_section_id: string;
    body: { landmark_name?: string; remarks?: string };
  }
>(
  "propertySections/updateMeta",
  async ({ property_section_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.patch(
        `${BaseURL}/api/v1/property-sections/${property_section_id}`,
        body
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to update property section meta"
      );
    }
  }
);

export const updatePropertySectionDocuments = createAsyncThunk<
  any,
  {
    property_section_id: string;
    body: { contractor_type?: string; category: string; files: any[] };
  }
>(
  "propertySections/updateDocuments",
  async ({ property_section_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.patch(
        `${BaseURL}/api/v1/property-sections/${property_section_id}/documents`,
        body
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to update documents"
      );
    }
  }
);

export const deletePropertySectionDocument = createAsyncThunk<
  any,
  { property_section_id: string; s3_key: string }
>(
  "propertySections/deleteDocument",
  async ({ property_section_id, s3_key }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.delete(
        `${BaseURL}/api/v1/property-sections/${property_section_id}/documents`,
        { data: { s3_key } }
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to delete document"
      );
    }
  }
);

export const deletePropertySections = createAsyncThunk<
  any,
  { property_section_ids: string[] }
>(
  "propertySections/delete",
  async ({ property_section_ids }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.delete(`${BaseURL}/api/v1/property-sections`, {
        data: { property_section_ids },
      });
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || "Failed to delete property sections"
      );
    }
  }
);

type PropertySectionsState = {
  creating: {
    loading: boolean;
    error: boolean;
    data: any;
  };
  list: {
    loading: boolean;
    error: boolean;
    data: any[];
  };
  current: {
    loading: boolean;
    error: boolean;
    data: any | null;
  };
};

const initialState: PropertySectionsState = {
  creating: {
    loading: false,
    error: false,
    data: null,
  },
  list: {
    loading: false,
    error: false,
    data: [],
  },
  current: {
    loading: false,
    error: false,
    data: null,
  },
};

export const propertySectionsSlice = createSlice({
  name: "propertySections",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(createPropertySection.pending, (state) => {
      state.creating.loading = true;
      state.creating.error = false;
    });
    builder.addCase(createPropertySection.fulfilled, (state, action) => {
      state.creating.loading = false;
      state.creating.data = action.payload;
    });
    builder.addCase(createPropertySection.rejected, (state) => {
      state.creating.loading = false;
      state.creating.error = true;
    });

    builder.addCase(fetchPropertySectionsByProperty.pending, (state) => {
      state.list.loading = true;
      state.list.error = false;
    });
    builder.addCase(
      fetchPropertySectionsByProperty.fulfilled,
      (state, action) => {
        state.list.loading = false;
        state.list.data = Array.isArray(action.payload) ? action.payload : [];
      }
    );
    builder.addCase(fetchPropertySectionsByProperty.rejected, (state) => {
      state.list.loading = false;
      state.list.error = true;
    });

    builder.addCase(fetchPropertySectionById.pending, (state) => {
      state.current.loading = true;
      state.current.error = false;
      state.current.data = null;
    });
    builder.addCase(fetchPropertySectionById.fulfilled, (state, action) => {
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(fetchPropertySectionById.rejected, (state) => {
      state.current.loading = false;
      state.current.error = true;
    });

    builder.addCase(appendContract.pending, (state) => {
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(appendContract.fulfilled, (state, action) => {
      state.current.loading = false;
      state.current.data = action.payload;
    });
    builder.addCase(appendContract.rejected, (state) => {
      state.current.loading = false;
      state.current.error = true;
    });

    builder.addCase(updateCommonAreaSection.pending, (state) => {
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(updateCommonAreaSection.fulfilled, (state, action) => {
      state.current.loading = false;
      const updated = action.payload;
      if (updated) {
        state.current.data = updated;
        if (Array.isArray(state.list.data)) {
          state.list.data = state.list.data.map((item: any) =>
            String(item?.property_section_id) ===
            String(updated.property_section_id)
              ? updated
              : item
          );
        }
      }
    });
    builder.addCase(updateCommonAreaSection.rejected, (state) => {
      state.current.loading = false;
      state.current.error = true;
    });

    builder.addCase(updatePropertySectionMeta.pending, (state) => {
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(updatePropertySectionMeta.fulfilled, (state, action) => {
      state.current.loading = false;
      const updated = action.payload;
      if (updated) {
        state.current.data = updated;
        if (Array.isArray(state.list.data)) {
          state.list.data = state.list.data.map((item: any) =>
            String(item?.property_section_id) ===
            String(updated.property_section_id)
              ? updated
              : item
          );
        }
      }
    });
    builder.addCase(updatePropertySectionMeta.rejected, (state) => {
      state.current.loading = false;
      state.current.error = true;
    });

    builder.addCase(updatePropertySectionDocuments.pending, (state) => {
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(
      updatePropertySectionDocuments.fulfilled,
      (state, action) => {
        state.current.loading = false;
        const updated = action.payload;
        if (updated) state.current.data = updated;
      }
    );
    builder.addCase(updatePropertySectionDocuments.rejected, (state) => {
      state.current.loading = false;
      state.current.error = true;
    });

    builder.addCase(deletePropertySectionDocument.pending, (state) => {
      state.current.loading = true;
      state.current.error = false;
    });
    builder.addCase(
      deletePropertySectionDocument.fulfilled,
      (state, action) => {
        state.current.loading = false;
        const updated = action.payload;
        if (updated) state.current.data = updated;
      }
    );
    builder.addCase(deletePropertySectionDocument.rejected, (state) => {
      state.current.loading = false;
      state.current.error = true;
    });

    // deletePropertySections
    builder.addCase(deletePropertySections.pending, (state) => {
      state.list.loading = true;
      state.list.error = false;
    });
    builder.addCase(deletePropertySections.fulfilled, (state, _action) => {
      state.list.loading = false;
    });
    builder.addCase(deletePropertySections.rejected, (state) => {
      state.list.loading = false;
      state.list.error = true;
    });
  },
});

export default propertySectionsSlice.reducer;
